import { RefObject, useCallback, useEffect, useMemo } from 'react';
import { MAX_ZOOM_VALUE, setBaseZoomLevel, setRecordingFps, setZoomValue } from '../store/zoomLevel';
import { getAbsolutePlayerTime, MIN_SEGMENT_WIDTH } from '../timeUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getSegmentsTrack } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils';
import { Clip } from '@/domains/asset';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { getClipFPS } from '@/libs/clipUtils';
import {
  getPlayerConfigIntro,
  getPlayerConfigOutro
} from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils/playerConfigGetters';

const getTotalVisibleDurationFrame = (clipData: Clip) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentsTrack = getSegmentsTrack(playerConfig);
  const totalVisibleDurationFrame = segmentsTrack.segments.reduce((acc, segment) => {
    if (segment.type === SegmentType.COMPOSITE) {
      return acc + segment?.currentBounds.toFrame - segment?.currentBounds.fromFrame;
    }
    return acc + (segment.toFrame - segment.fromFrame);
  }, 0);
  return totalVisibleDurationFrame;
};

export const useTimelineZoomAdjustment = ({
  speakerTimelineContainerRef
}: {
  speakerTimelineContainerRef: RefObject<HTMLDivElement>;
}) => {
  const { clipData, clipId, fps } = useClipsContext();

  const visibleTimelineFrames = useMemo(() => getTotalVisibleDurationFrame(clipData), [clipData]);

  const getCurrentZoomValue = useCallback(
    (clipData: Clip) => {
      if (!speakerTimelineContainerRef.current) {
        return;
      }
      const TOLERANCE_IN_PIXELS_AT_END = 10;
      const CONTAINER_PADDING = 12 * 2;
      const TIMELINE_CONTAINER_EXCLUDE_WIDTH = CONTAINER_PADDING + TOLERANCE_IN_PIXELS_AT_END;

      let timelineContainerWidth = speakerTimelineContainerRef.current.clientWidth - TIMELINE_CONTAINER_EXCLUDE_WIDTH;

      if (!timelineContainerWidth) {
        return;
      }

      const playerConfig = clipData.asset_metadata.player_config;

      if (!playerConfig) {
        return;
      }
      const INTRO_OUTRO_MIN_WIDTH_BUFFER = 54;

      const intro = getPlayerConfigIntro(playerConfig);
      const outro = getPlayerConfigOutro(playerConfig);
      if (!intro) {
        timelineContainerWidth -= INTRO_OUTRO_MIN_WIDTH_BUFFER;
      }

      if (!outro) {
        timelineContainerWidth -= INTRO_OUTRO_MIN_WIDTH_BUFFER;
      }

      if (!clipData.asset_metadata.player_config?.durationInFrames) {
        return;
      }

      // Binary search to find the optimal zoom value
      let minZoom = 0.0001;
      let maxZoom = MAX_ZOOM_VALUE;
      let optimalZoom = minZoom;
      const tolerance = 0.0001; // Tolerance for convergence

      while (maxZoom - minZoom > tolerance) {
        const midZoom = (minZoom + maxZoom) / 2;
        // Convert to pixels (assuming 1 second = fps frames, and 1 frame = midZoom pixels)
        const fps = getClipFPS(clipData);

        // Calculate the final length with this zoom value
        const finalLength = getAbsolutePlayerTime(
          clipData.asset_metadata.player_config.durationInFrames / fps,
          clipData,
          MIN_SEGMENT_WIDTH,
          midZoom
        );

        const timelineLengthInPixels = finalLength * fps * midZoom;
        optimalZoom = midZoom;
        if (Math.abs(timelineContainerWidth - timelineLengthInPixels) < 10) {
          break;
        } else if (timelineLengthInPixels > timelineContainerWidth) {
          // Timeline is too long, reduce zoom
          maxZoom = midZoom;
        } else {
          // Timeline is too short, increase zoom
          minZoom = midZoom;
        }
      }

      return optimalZoom;
    },
    [speakerTimelineContainerRef]
  );

  useEffect(() => {
    if (clipData) {
      const currentZoomValue = getCurrentZoomValue(clipData);
      if (currentZoomValue) {
        setRecordingFps(fps);
        setZoomValue(currentZoomValue);
      }
    }
  }, [clipId, speakerTimelineContainerRef]);

  useEffect(() => {
    if (clipData) {
      const currentZoomValue = getCurrentZoomValue(clipData);
      if (currentZoomValue) {
        setRecordingFps(fps);
        setBaseZoomLevel(Math.min(MAX_ZOOM_VALUE / 2, currentZoomValue));
      }
    }
  }, [visibleTimelineFrames]);
};
